import { User } from 'oidc-client-ts';
import { createApiClient } from '../oidc-api';
import {
  HealthProgramGroupDto,
  HealthProgramGroupCalendarDto,
  // HealthProgramGroupUserDto, // Não está sendo usado
  HealthProgramGroupBatchMessageDto,
  PaginatedHealthProgramGroupResponse,
  PaginatedHealthProgramGroupUserResponse,
  PaginatedHealthProgramGroupCalendarResponse
} from './types';

// Create a variable to store the current user
let currentUser: User | null = null;

// Function to set the current user
export const setCurrentUser = (user: User | null) => {
  currentUser = user;
};

class HealthService {
  private getApi() {
    return createApiClient(currentUser);
  }

  // Program Groups
  async getProgramGroups(
    pageNumber = 1,
    pageSize = 10,
    sortBy?: string,
    ascending?: boolean
  ): Promise<PaginatedHealthProgramGroupResponse> {
    const api = this.getApi();
    let requestUrl = `/v1/health/program_groups?page_number=${pageNumber}&page_size=${pageSize}`;
    
    if (sortBy) {
      requestUrl += `&sort_by=${sortBy}`;
    }
    
    if (ascending !== undefined) {
      requestUrl += `&ascending=${ascending}`;
    }
    
    return api.get(requestUrl);
  }

  async getProgramGroupById(id: string): Promise<HealthProgramGroupDto> {
    const api = this.getApi();
    return api.get(`/v1/health/program_groups/${id}`);
  }

  async createProgramGroup(programGroup: HealthProgramGroupDto): Promise<HealthProgramGroupDto> {
    const api = this.getApi();
    return api.post('/v1/health/program_groups', programGroup);
  }

  async updateProgramGroup(id: string, programGroup: HealthProgramGroupDto): Promise<HealthProgramGroupDto> {
    const api = this.getApi();
    return api.put(`/v1/health/program_groups/${id}`, programGroup);
  }

  // Program Group Calendars
  async getProgramGroupCalendars(
    programGroupId: string,
    pageNumber = 1,
    pageSize = 10,
    sortBy?: string,
    ascending?: boolean
  ): Promise<PaginatedHealthProgramGroupCalendarResponse> {
    const api = this.getApi();
    let requestUrl = `/v1/health/program_groups/${programGroupId}/calendars?page_number=${pageNumber}&page_size=${pageSize}`;
    
    if (sortBy) {
      requestUrl += `&sort_by=${sortBy}`;
    }
    
    if (ascending !== undefined) {
      requestUrl += `&ascending=${ascending}`;
    }
    
    return api.get(requestUrl);
  }
  
  async createProgramGroupCalendar(
    programGroupId: string,
    calendar: HealthProgramGroupCalendarDto
  ): Promise<HealthProgramGroupCalendarDto> {
    const api = this.getApi();
    return api.post(`/v1/health/program_groups/${programGroupId}/calendars`, calendar);
  }

  async updateProgramGroupCalendar(
    programGroupId: string,
    calendarId: string,
    calendar: HealthProgramGroupCalendarDto
  ): Promise<HealthProgramGroupCalendarDto> {
    const api = this.getApi();
    return api.put(
      `/v1/health/program_groups/${programGroupId}/calendars/${calendarId}`,
      calendar
    );
  }

  async deleteProgramGroupCalendar(programGroupId: string, calendarId: string): Promise<void> {
    const api = this.getApi();
    return api.delete(`/v1/health/program_groups/${programGroupId}/calendars/${calendarId}`);
  }

  // Program Group Users
  async getProgramGroupUsers(
    programGroupId: string,
    pageNumber = 1,
    pageSize = 10,
    sortBy?: string,
    ascending?: boolean
  ): Promise<PaginatedHealthProgramGroupUserResponse> {
    const api = this.getApi();
    let requestUrl = `/v1/health/program_groups/${programGroupId}/users?page_number=${pageNumber}&page_size=${pageSize}`;
    
    if (sortBy) {
      requestUrl += `&sort_by=${sortBy}`;
    }
    
    if (ascending !== undefined) {
      requestUrl += `&ascending=${ascending}`;
    }
    
    return api.get(requestUrl);
  }

  async attachProgramGroupUser(programGroupId: string, userId: string): Promise<void> {
    const api = this.getApi();
    return api.post(`/v1/health/program_groups/${programGroupId}/users/${userId}/attach`, {});
  }

  async detachProgramGroupUser(programGroupId: string, userId: string): Promise<void> {
    const api = this.getApi();
    return api.delete(`/v1/health/program_groups/${programGroupId}/users/${userId}/detach`);
  }

  async attachProgramGroupUserSync(programGroupId: string, userId: string): Promise<void> {
    const api = this.getApi();
    return api.post(`/v1/health/program_groups/${programGroupId}/users/${userId}/schedule_sync`, {});
  }

  // Batch Messages
  async dispatchProgramGroupBatchMessages(
    programGroupId: string,
    batchMessage: HealthProgramGroupBatchMessageDto
  ): Promise<void> {
    const api = this.getApi();
    return api.post(`/v1/health/program_groups/${programGroupId}/batch_messages`, batchMessage);
  }

  // Synchronize Users Calendars
  async synchronizeUsersCalendars(programGroupId: string): Promise<void> {
    const api = this.getApi();
    return api.post(`/v1/health/program_groups/${programGroupId}/synchronize_users_calendars`, {});
  }
}

export const healthService = new HealthService();